/**
 * dymoParser — čistý parser USB-HID reportu váhy DYMO Digital Postal Scale M5
 * (a příbuzné M-řady). Třída "POS Scale", Usage Page 0x8D, VID 0x0922.
 *
 * DŮLEŽITÉ — posun indexů: WebHID předává reportId v `event.reportId`, takže
 * `event.data` (DataView) reportId NEOBSAHUJE. Indexy jsou tedy:
 *   byte 0 = status, 1 = unit, 2 = scaling_exponent (signed), 3-4 = weight (uint16 LE).
 * (Na blozích, které reportId počítají do dat, jsou indexy o 1 vyšší.)
 *
 * Modul je čistý (bez DOM/WebHID) → unit-testovatelný bez HW.
 */

export const DYMO_VENDOR_ID = 0x0922; // Dymo-CoStar Corp.
export const DYMO_USAGE_PAGE = 0x8d; // Point of Sale — Weighing Devices

// Scale Status (USB HID POS tabulka)
export const STATUS = {
  FAULT: 1,
  STABLE_ZERO: 2, // stabilní na nule (vynulováno)
  IN_MOTION: 3, // čtení se ustaluje
  STABLE: 4, // stabilní kladná hmotnost
  UNDER_ZERO: 5, // záporná hodnota
  OVER_WEIGHT: 6, // přetížení
  NEEDS_CALIBRATION: 7,
  NEEDS_REZEROING: 8,
};

// Weight Unit (USB HID POS tabulka)
export const UNIT = {
  MILLIGRAM: 1,
  GRAM: 2,
  KILOGRAM: 3,
  OUNCE: 11,
  POUND: 12,
};

/**
 * Převede surovou váhu (uint16) × 10^exponent na gramy podle jednotky.
 * Vrací null pro neznámou jednotku.
 */
export function rawToGrams(rawWeight, exponent, unit) {
  const value = rawWeight * Math.pow(10, exponent);
  switch (unit) {
    case UNIT.GRAM: return value;
    case UNIT.OUNCE: return value * 28.349523125;
    case UNIT.POUND: return value * 453.59237;
    case UNIT.MILLIGRAM: return value / 1000;
    case UNIT.KILOGRAM: return value * 1000;
    default: return null;
  }
}

/**
 * Naparsuje DYMO input report.
 * @param {DataView} dataView — `event.data` z WebHID inputreport (BEZ reportId).
 * @returns {{status:number, unit:number, exponent:number, rawWeight:number,
 *            grams:number|null, stable:boolean, isGram:boolean}}
 */
export function parseDymoReport(dataView) {
  if (!dataView || typeof dataView.getUint8 !== 'function' || dataView.byteLength < 5) {
    const len = dataView && dataView.byteLength != null ? dataView.byteLength : 0;
    throw new Error(`Neplatný DYMO report (délka ${len} B, očekáváno ≥ 5).`);
  }
  const status = dataView.getUint8(0);
  const unit = dataView.getUint8(1);
  const exponent = dataView.getInt8(2); // signed: 0xFF → -1
  const rawWeight = dataView.getUint16(3, true); // little-endian (LSB, MSB)

  let grams = rawToGrams(rawWeight, exponent, unit);
  if (grams !== null && status === STATUS.UNDER_ZERO) grams = -grams;

  return {
    status,
    unit,
    exponent,
    rawWeight,
    grams,
    stable: status === STATUS.STABLE,
    isGram: unit === UNIT.GRAM,
  };
}
